import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNav from '../../components/layout/TopNav';
import { useVideoStore, useAuthStore } from '../../store';
import api from '../../services/api';
import { UserRole } from '../../types';
import type { ScoringStats, Grade } from '../../types';
import { useProcessingPoller } from '../../hooks/useProcessingPoller';

const GRADE_COLORS: Record<string, string> = {
  A: 'bg-secondary/10 text-secondary border-secondary/20',
  B: 'bg-primary/10 text-primary border-primary/20',
  C: 'bg-tertiary/10 text-tertiary border-tertiary/20',
  D: 'bg-orange-50 text-orange-500 border-orange-200',
  F: 'bg-error/10 text-error border-error/20',
};

const BAR_COLORS: Record<string, string> = {
  A: 'bg-secondary',
  B: 'bg-primary',
  C: 'bg-tertiary',
  D: 'bg-orange-400',
  F: 'bg-error',
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { videos, setVideos, totalVideos } = useVideoStore();
  const [stats, setStats] = useState<ScoringStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  const [coachingCount, setCoachingCount] = useState(0);
  const userRole = useAuthStore((s) => s.user?.role);
  const isSalesRep = userRole === UserRole.SALES_REP;

  const fetchData = useCallback(async () => {
    try {
      const [videosRes, statsRes] = await Promise.all([
        api.get('/videos', { params: { page: 1, perPage: 10 } }),
        api.get('/scoring/stats'),
      ]);
      const videoList = videosRes.data.data;
      setVideos(videoList, videosRes.data.meta?.total ?? 0);
      setStats(statsRes.data.data);

      // Fetch presigned thumbnail URLs for the recent activity grid
      const ids = videoList.map((v: { id: string }) => v.id);
      if (ids.length > 0) {
        const urlsRes = await api.post('/videos/thumbnail-urls', { videoIds: ids });
        setThumbnailUrls(urlsRes.data.data ?? {});
      }

      if (isSalesRep) {
        const coachingRes = await api.get('/coaching/me/unacknowledged-count');
        setCoachingCount(coachingRes.data.data?.count ?? 0);
      }
    } catch {
      // Silently fail — show empty state
    } finally {
      setLoading(false);
    }
  }, [setVideos, isSalesRep]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh when any PROCESSING/PENDING video completes in the background
  const { processingStages } = useProcessingPoller(videos, fetchData);

  const gradeTotal = stats
    ? Object.values(stats.gradeDistribution).reduce((a, b) => a + b, 0)
    : 0;
  const gradePct = (g: Grade) =>
    gradeTotal > 0 ? Math.round(((stats?.gradeDistribution[g] ?? 0) / gradeTotal) * 100) : 0;

  // Derived metrics
  const avgScore = stats?.averageScore ?? null;
  const avgGrade = avgScore === null ? '—'
    : avgScore >= 85 ? 'A'
    : avgScore >= 70 ? 'B'
    : avgScore >= 55 ? 'C'
    : avgScore >= 40 ? 'D' : 'F';

  const thisWeek = stats?.thisWeekCount ?? 0;
  const lastWeek = stats?.lastWeekCount ?? 0;

  const totalTrend = lastWeek > 0
    ? `${thisWeek >= lastWeek ? '+' : ''}${Math.round(((thisWeek - lastWeek) / lastWeek) * 100)}% wk`
    : thisWeek > 0 ? `+${thisWeek} this wk` : '—';
  const totalTrendColor = lastWeek > 0 && thisWeek < lastWeek ? 'text-red-400' : 'text-emerald-500';

  // Pass rate: % of scored videos grading D or above (only F = fail)
  const dist = (stats?.gradeDistribution ?? {}) as Record<string, number>;
  const passCount = (dist['A'] ?? 0) + (dist['B'] ?? 0) + (dist['C'] ?? 0) + (dist['D'] ?? 0);
  const scoredTotal = Object.values(dist).reduce((a: number, b: number) => a + b, 0);
  const passRate = scoredTotal > 0 ? Math.round((passCount / scoredTotal) * 100) : null;
  const passRateColor = passRate === null ? 'text-slate-400'
    : passRate >= 70 ? 'text-emerald-500'
    : passRate >= 40 ? 'text-amber-500'
    : 'text-red-400';

  const flaggedCount = stats?.flaggedVideoCount ?? 0;

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 transition-colors duration-300 font-sans flex flex-col">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Sales Performance</h1>
            <p className="text-slate-500 dark:text-slate-400 font-medium text-base mt-2">
              Technical breakdown of video quality and engagement.
            </p>
          </div>
          {isSalesRep && (
            <button
              onClick={() => navigate('/upload')}
              className="h-11 px-8 rounded-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/10 hover:shadow-xl hover:shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-[20px]">cloud_upload</span>
              Upload Video
            </button>
          )}
        </div>

        {/* Coaching Callout — SALES_REP only, when unacknowledged notes exist */}
        {isSalesRep && coachingCount > 0 && (
          <div
            onClick={() => navigate('/coaching')}
            className="flex items-center justify-between gap-4 mb-8 p-5 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/30 cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-950/40 transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-emerald-500/10 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-emerald-500 text-[22px]">school</span>
              </div>
              <div>
                <p className="text-[11px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                  Coaching Feedback
                </p>
                <p className="text-xs text-emerald-600/80 dark:text-emerald-500/80 mt-0.5">
                  You have <strong>{coachingCount}</strong> unreviewed coaching {coachingCount === 1 ? 'tip' : 'tips'} — click to review
                </p>
              </div>
            </div>
            <span className="material-symbols-outlined text-emerald-500 text-[20px] group-hover:translate-x-1 transition-transform">
              chevron_right
            </span>
          </div>
        )}

        {/* Metric Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {[
            { label: 'Total Videos', value: totalVideos.toLocaleString(), suffix: totalTrend, icon: lastWeek > 0 && thisWeek < lastWeek ? 'trending_down' : 'trending_up', color: totalTrendColor, href: '/videos' },
            { label: 'Average VQS Score', value: avgScore !== null ? avgScore : '—', suffix: avgGrade, icon: 'auto_graph', color: 'text-primary', href: '/videos?status=SCORED' },
            { label: 'Pass Rate', value: passRate !== null ? `${passRate}%` : '—', suffix: passRate !== null ? `${passCount} of ${scoredTotal}` : 'No scored videos', icon: passRate !== null && passRate >= 70 ? 'verified' : 'pending', color: passRateColor, href: '/videos?status=SCORED' },
            { label: 'Flagged Videos', value: flaggedCount, suffix: flaggedCount === 0 ? 'All Clear' : 'Review', icon: flaggedCount === 0 ? 'verified' : 'flag', color: flaggedCount === 0 ? 'text-emerald-500' : 'text-red-400', href: '/videos?hasFlags=true' },
          ].map((stat, idx) => (
            <div key={stat.label} onClick={() => navigate(stat.href)} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all group overflow-hidden relative border-b-4 border-b-transparent hover:border-b-primary/40 cursor-pointer hover:scale-[1.02] active:scale-[0.99]">
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 dark:bg-primary/20 rounded-full -mr-12 -mt-12 transition-transform group-hover:scale-110" />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 block mb-4">{stat.label}</span>
              <div className="flex items-center justify-between relative z-10">
                <div className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">{stat.value}</div>
                {idx === 1 ? (
                  <div className={`px-3 py-1 rounded-xl font-bold text-sm shadow-lg ${avgGrade === 'A' ? 'bg-emerald-500 text-white shadow-emerald-500/20' : avgGrade === 'B' ? 'bg-primary text-white shadow-primary/20' : avgGrade === 'C' ? 'bg-amber-500 text-white shadow-amber-500/20' : avgGrade === 'D' ? 'bg-orange-400 text-white shadow-orange-400/20' : avgGrade === '—' ? 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 shadow-none' : 'bg-red-500 text-white shadow-red-500/20'}`}>
                    {stat.suffix}
                  </div>
                ) : (
                  <div className={`flex items-center ${stat.color} font-bold text-[10px] uppercase tracking-widest`}>
                    <span className="material-symbols-outlined text-[16px] mr-1">{stat.icon}</span>
                    <span>{stat.suffix}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Main Content Sections */}
        <div className="space-y-8">
          {/* Table section - Full Width */}
          <div className="bg-white/40 dark:bg-black/20 border border-slate-100 dark:border-white/5 rounded-2xl shadow-sm overflow-hidden p-8">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4 px-2">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight uppercase tracking-widest">
                {isSalesRep ? 'My Recent Uploads' : 'Recent Activity'}
              </h2>
            </div>
            <div className="overflow-x-auto lg:overflow-x-visible">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-50 dark:border-white/5">
                    <th className="px-6 py-5 text-left text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Video Content</th>
                    {!isSalesRep && (
                      <th className="px-6 py-5 text-left text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Owner</th>
                    )}
                    <th className="px-6 py-5 text-left text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Score</th>
                    <th className="px-6 py-5 text-left text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Grade</th>
                    <th className="px-6 py-5 text-left text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Status</th>
                    <th className="px-6 py-5 text-right text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-white/5">
                  {loading ? (
                    <tr>
                      <td colSpan={isSalesRep ? 5 : 6} className="px-8 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-6 h-6 border-2 border-primary border-t-transparent animate-spin rounded-full" />
                          <span className="text-sm font-semibold text-slate-400 dark:text-slate-500 whitespace-nowrap">Loading catalog...</span>
                        </div>
                      </td>
                    </tr>
                  ) : videos.length === 0 ? (
                    <tr>
                      <td colSpan={isSalesRep ? 5 : 6} className="px-8 py-16 text-center">
                        <div className="flex flex-col items-center gap-3 text-slate-400 dark:text-slate-600">
                          <span className="material-symbols-outlined text-4xl">movie_off</span>
                          <p className="text-sm font-semibold">
                            {isSalesRep ? 'No uploads yet — initiate your first capture' : 'No activity detected'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    videos.slice(0, 8).map((video) => {
                      const processingLabel = processingStages[video.id] ?? 'Processing';
                      const statusConfig: Record<string, { color: string; text: string }> = {
                        PENDING: { color: 'text-slate-400 dark:text-slate-500', text: 'Pending' },
                        PROCESSING: { color: 'text-primary dark:text-primary/90', text: processingLabel },
                        SCORED: { color: 'text-emerald-500 dark:text-emerald-400', text: 'Scored' },
                        FLAGGED: { color: 'text-red-500 dark:text-red-400', text: 'Flagged' },
                        FAILED: { color: 'text-slate-500 dark:text-slate-400', text: 'Failed' },
                      };
                      const sc = statusConfig[video.scoringStatus] ?? statusConfig.PENDING;

                      const isClickable = video.scoringStatus !== 'PENDING' && video.scoringStatus !== 'PROCESSING';

                      return (
                        <tr
                          key={video.id}
                          className={`transition-all group ${isClickable ? 'hover:bg-slate-50/50 dark:hover:bg-white/5 cursor-pointer' : 'cursor-default opacity-60'}`}
                          onClick={() => isClickable && navigate(`/videos/${video.id}`)}
                        >
                          <td className="px-8 py-6">
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 group-hover:border-primary/30 transition-all overflow-hidden shrink-0 relative">
                                {thumbnailUrls[video.id] ? (
                                  <img
                                    src={thumbnailUrls[video.id]}
                                    alt={video.title}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-slate-400 dark:text-slate-500 group-hover:bg-white dark:group-hover:bg-slate-700 transition-all">
                                    <span className="material-symbols-outlined text-[20px]">play_circle</span>
                                  </div>
                                )}
                                <div className="absolute bottom-0 left-0 h-0.5 bg-primary w-0 group-hover:w-full transition-all duration-300" />
                              </div>
                              <div className="flex flex-col min-w-0 flex-1 max-w-md">
                                <span className="text-sm font-medium text-slate-900 dark:text-white whitespace-normal break-words leading-snug group-hover:text-primary transition-colors">
                                  {video.title}
                                </span>
                              </div>
                            </div>
                          </td>
                          {!isSalesRep && (
                            <td className="px-8 py-6">
                              <span className="text-sm font-medium text-slate-900 dark:text-white">
                                {video.uploader
                                  ? `${video.uploader.firstName ?? ''} ${video.uploader.lastName ?? ''}`.trim() || video.uploader.email
                                  : '-'}
                              </span>
                            </td>
                          )}
                          <td className="px-8 py-6">
                            {video.videoScore ? (
                              <span className="text-sm font-bold text-primary">
                                {video.videoScore.compositeScore}
                              </span>
                            ) : (
                              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">-</span>
                            )}
                          </td>
                          <td className="px-8 py-6">
                            {video.videoScore ? (
                              <div className={`px-2 py-0.5 rounded-lg text-[10px] font-black tracking-widest w-fit border ${GRADE_COLORS[video.videoScore.grade] ?? ''}`}>
                                {video.videoScore.grade}
                              </div>
                            ) : (
                              <span className="text-sm font-medium text-slate-900 dark:text-white">-</span>
                            )}
                          </td>
                          <td className="px-8 py-6">
                            <span className={`text-[10px] font-medium uppercase tracking-[0.1em] ${sc.color}`}>
                              {sc.text}
                            </span>
                          </td>
                          <td className="px-8 py-6 text-right">
                            <span className="text-sm font-medium text-slate-900 dark:text-white whitespace-nowrap">
                              {new Date(video.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Secondary Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {/* Grade Distribution */}
            <div className="xl:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 shadow-sm">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined text-[24px]">analytics</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight uppercase tracking-widest">Grading Matrix</h3>
              </div>
              
              <div className="space-y-5">
                {([['A', 'Exceptional'], ['B', 'Proficient'], ['C', 'Marginal'], ['D', 'Critical']] as const).map(([grade, label]) => (
                  <div key={grade} className="flex items-center gap-4">
                    <div className={`w-9 h-9 flex items-center justify-center font-black text-sm border rounded-xl shrink-0 ${GRADE_COLORS[grade]}`}>
                      {grade}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">{label}</span>
                        <span className="text-[10px] font-black text-slate-500 dark:text-slate-400">{gradePct(grade as Grade)}%</span>
                      </div>
                      <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${BAR_COLORS[grade]} rounded-full transition-all duration-1000`}
                          style={{ width: `${gradePct(grade as Grade)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Technical Insight */}
            <div className="xl:col-span-1 bg-primary/5 dark:bg-primary/10 border border-primary/10 dark:border-primary/20 rounded-2xl p-8 relative overflow-hidden group flex flex-col justify-center">
              <div className="absolute -right-4 -bottom-4 w-32 h-32 bg-primary/5 dark:bg-primary/10 rounded-full blur-2xl group-hover:bg-primary/10 transition-all" />
              <div className="flex items-start gap-5 relative">
                <div className="w-12 h-12 rounded-2xl bg-white dark:bg-slate-800 shadow-sm flex items-center justify-center text-primary shrink-0 transition-transform group-hover:scale-110">
                  <span className="material-symbols-outlined text-[28px] fill-current">auto_awesome</span>
                </div>
                <div className="space-y-2">
                  <h4 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">AI Insights</h4>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed">
                    {stats?.insight ??
                      'Videos that walk through all six vehicle zones and end with a clear call to action consistently score higher. If you want a quick win, try showing more of the car and closing with a direct next step.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
